import React from 'react';

interface ChartData {
  month: string;
  amount: number;
}

interface CommissionChartProps {
  data: ChartData[];
  className?: string;
}

const CommissionChart: React.FC<CommissionChartProps> = ({
  data,
  className = ''
}) => {
  // Find the maximum value for scaling
  const hasData = data && data.length > 0 && data.some(d => typeof d.amount === 'number' && !isNaN(d.amount));
  const maxAmount = hasData ? Math.max(...data.map(d => d.amount)) : 0;
  const minAmount = hasData ? Math.min(...data.map(d => d.amount)) : 0;
  const range = maxAmount - minAmount || 1;

  // Calculate bar heights (percentage of container)
  const getBarHeight = (amount: number) => {
    if (maxAmount === 0) return 0;
    return ((amount - minAmount) / range) * 80 + 10; // 10-90% range
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-end justify-between h-64 bg-gray-50 rounded-lg p-4">
        {hasData ? data.map((item, index) => (
          <div key={index} className="flex flex-col items-center flex-1 mx-1">
            {/* Bar */}
            <div className="relative w-full max-w-12 flex items-end justify-center h-48">
              <div
                className="bg-gradient-to-t from-primary to-secondary rounded-t-md w-full transition-all duration-300 hover:opacity-80 relative group"
                style={{ height: `${getBarHeight(item.amount)}%` }}
              >
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    ₦{item.amount.toLocaleString()}
                  </div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
            {/* Month label */}
            <div className="mt-2 text-xs text-gray-600 text-center">
              {item.month.length === 4 && /^\d{4}$/.test(item.month)
                ? item.month // Show full year if it's a year
                : item.month.substring(0, 3)}
            </div>
          </div>
        )) : (
          <div className="w-full text-center text-gray-400">No commission data available</div>
        )}
      </div>
      {/* Y-axis labels */}
      {hasData && (
        <div className="flex justify-between mt-4 text-sm text-gray-500">
          <span>₦{minAmount.toLocaleString()}</span>
          <span>₦{maxAmount.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
};

export default CommissionChart;